import { Offcanvas, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import { EditEntityDrawerProps } from './EntityListProps';
import {
  IncidentTactic,
  useEditIncidentTactic,
  useIncidentTactics,
} from './hooks/incidentTacticHooks';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import FormTextField from './formFields/FormTextField';
import FormTextAreaField from './formFields/FormTextAreaField';
import { ParentRelationships } from './ParentRelationships';
import { useIncidentStrategies } from './hooks/incidentStrategyHooks';
import RelationshipPickerField from './formFields/RelationshipPickerField';
import { useWorkAssignments } from './hooks/workAssignmentHooks';
import { useIncidentResourceRequirements } from './hooks/incidentResourceRequirementHooks';
import { Tactic, useEditTactic } from './hooks/tacticHooks';
import { INCIDENT } from './ICSConstants';
import { isIncidentTactic } from './utils/typeguard';
import { TacticBase } from '../../types/iapBaseTypes';
import IncidentResourceRequirementAddDrawer from './IncidentResourceRequirementAddDrawer';

type FormFields = TacticBase & {
  incident_resources?: UUID[];
};

const TacticEditDrawer: React.FC<
  EditEntityDrawerProps<IncidentTactic | Tactic>
> = ({ toggle, selectedEntity: editedEntity, scope = INCIDENT }) => {
  const [
    showAddResourceRequirementDrawer,
    setShowAddResourceRequirementDrawer,
  ] = useState(false);
  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      name: editedEntity?.name ?? '',
      description: editedEntity?.description ?? '',
      incident_resources:
        'incident_resources' in editedEntity
          ? editedEntity.incident_resources
          : [],
    },
  });
  const { handleSubmit, formState } = form;

  const { data: incidentTactics } = useIncidentTactics(scope);
  const currentTactic =
    isIncidentTactic(editedEntity, scope) && incidentTactics
      ? incidentTactics.find(
          (tactic) => tactic.tactic_id === editedEntity.tactic_id
        ) || editedEntity
      : editedEntity;

  const incidentStrategies = useIncidentStrategies(scope, (iss) => {
    return 'strategies' in currentTactic
      ? currentTactic.strategies
          .map((strategyId: UUID) =>
            iss.find((is) => is.strategy_id === strategyId)
          )
          .filter((x: any): x is NonNullable<typeof x> => !!x)
      : [];
  });

  const { data: workAssignmentOptions } = useWorkAssignments();
  const { data: incidentResourceOptions } = useIncidentResourceRequirements();

  const filteredIRRs = incidentResourceOptions?.filter((irr) => !irr.archived);

  const id =
    'tactic_id' in editedEntity ? editedEntity.tactic_id : editedEntity.id;
  const incidentMutation = useEditIncidentTactic(id);
  const organizationMutation = useEditTactic();
  const isIncident = isIncidentTactic(editedEntity, scope);

  // For work assignments, we need to use the incident tactic ID, not the base tactic ID
  const incidentTacticId =
    'id' in editedEntity && isIncident ? editedEntity.id : null;

  const tacticWorkAssignments = workAssignmentOptions?.filter(
    (wa) =>
      !wa.archived &&
      (wa.incident_tactic_id === incidentTacticId ||
        (incidentTacticId && wa.parent_tactics?.includes(incidentTacticId)))
  );

  const onNewTacticDataSubmit = async (formData: FormFields) => {
    let payload;
    if (isIncident) {
      payload = {
        tactic_id: id,
        name: formData.name,
        description: formData.description,
        archived: editedEntity.archived,
        strategies:
          currentTactic && 'strategies' in currentTactic
            ? currentTactic.strategies ?? []
            : [],
        work_assignments: [], // Keep empty - work assignments are managed via incident_tactic_id on work assignments
        incident_resources: formData.incident_resources || [],
      };
      await incidentMutation.mutateAsync(payload);
    } else {
      payload = {
        id,
        name: formData.name,
        description: formData.description,
        archived: editedEntity.archived,
      };
      await organizationMutation.mutateAsync(payload);
    }
    toggle();
  };

  const toggleActive = async () => {
    if (currentTactic?.strategies.length > 0) {
      return;
    }

    if (isIncident) {
      await incidentMutation.mutateAsync({
        ...editedEntity,
        strategies:
          currentTactic && 'strategies' in currentTactic
            ? currentTactic.strategies ?? []
            : [],
        archived: !editedEntity.archived,
      });
    } else {
      await organizationMutation.mutateAsync({
        ...(editedEntity as Tactic),
        archived: !editedEntity.archived,
      });
    }
    toggle();
  };

  return (
    <DrawerWrapper toggle={toggle} title="Edit Tactic">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onNewTacticDataSubmit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 300,
                    message: 'The maximum length is 300 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextAreaField
                title="Description"
                fieldName="description"
                rules={{
                  maxLength: {
                    value: 300,
                    message: 'The maximum length is 300 characters',
                  },
                }}
                variant="full"
              />
            </Row>
            {isIncident && (
              <Row>
                <ParentRelationships
                  label="Strategies"
                  parents={incidentStrategies.data}
                />
              </Row>
            )}
            {isIncident && (
              <Row>
                <ParentRelationships
                  label="Work Assignments"
                  parents={tacticWorkAssignments?.map((wa) => ({
                    name: wa.work_assignment,
                  }))}
                />
              </Row>
            )}
            {isIncident && 'incident_resources' in editedEntity && (
              <Row
                style={{
                  maxHeight: '50vh',
                  overflow: 'auto',
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-bold">
                    Incident Resource Requirements
                  </span>
                  <StylishNewButton
                    className="button--secondary button--small"
                    onClick={() => setShowAddResourceRequirementDrawer(true)}
                    type="button"
                  >
                    + Add Resource Requirement
                  </StylishNewButton>
                </div>
                <RelationshipPickerField
                  label={''}
                  fieldName={'incident_resources'}
                  defaultValue={editedEntity.incident_resources}
                  identity={(entity) => entity?.incident_resource_id}
                  options={filteredIRRs}
                  cardType={'incident-resource-requirements'}
                />
              </Row>
            )}
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--tertiary button--reverse"
                onClick={toggleActive}
                disabled={
                  isIncident &&
                  'strategies' in currentTactic &&
                  currentTactic.strategies.length > 0
                }
              >
                {(!editedEntity.archived && 'Deactivate') || 'Activate'}
              </StylishNewButton>
              <StylishNewButton
                className="button--secondary button--reverse"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={
                  (isIncidentTactic(editedEntity, scope)
                    ? incidentMutation.isPending
                    : organizationMutation.isPending) ||
                  !formState.isDirty ||
                  editedEntity.archived
                }
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
      {showAddResourceRequirementDrawer && (
        <IncidentResourceRequirementAddDrawer
          toggle={() => setShowAddResourceRequirementDrawer(false)}
        />
      )}
    </DrawerWrapper>
  );
};

export default TacticEditDrawer;
